import { useState } from 'react';

// Redux
import { useDispatch, useSelector } from 'react-redux';
import actions from 'store/actions';
import selectors from 'store/selectors';

// MUI
import {
  Box,
  Divider,
  Stack,
  Typography,
  IconButton,
  Grid,
} from '@mui/material';
// Icons
import CloseRounded from '@mui/icons-material/CloseRounded';
import DownloadIcon from '@mui/icons-material/SaveAlt';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

// Custom components
import CustomModal from 'components/CustomModal';
import UserAvatar from 'components/Avatar';
import CustomToolTip from 'components/Tooltip';
import { iconToComponentMapping } from 'pages/checklistV2/mappings';
import ZoomInIcon from 'components/Icons/zoomInIcon';
import ZoomOutIcon from 'components/Icons/zoomOutIcon';

// Styles
import {
  IconContainer,
  ArrowsContainer,
  ImageContainer,
  HeaderContent,
  ButtonsContainer,
  ImageBox,
} from './style';

// Constants
import DIALOGS from 'utils/dialogIds';
import {
  downloadFile,
  getFileName,
  getUrlExtension,
  getTimeZone,
} from 'utils/globalFunction';

// Slider
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// Moment
import moment from 'moment-timezone';

const GalleryDetailsModal = () => {
  const dispatch = useDispatch();
  const tz: string = getTimeZone();
  const dialogId = DIALOGS.GALLERY_DETAILS_MODAL;
  const _dialogState: any = useSelector(selectors.getDialogState);
  const dialogState = _dialogState?.[dialogId];
  const dialogData = dialogState?.data;
  const open = dialogState?.open;

  const [zoomLevels, setZoomLevels] = useState(
    dialogData?.allAttachments.map(() => 1),
  );
  const initialSlide = dialogData?.allAttachments?.findIndex(
    (attachment) => attachment?.image === dialogData?.id,
  );
  const [currentSlide, setCurrentSlide] = useState(initialSlide);

  const handleClose = () => {
    dispatch(actions.closeDialog(dialogId));
  };
  const handleZoomIn = () => {
    setZoomLevels((prevZoomLevels) => {
      const newZoomLevels = [...prevZoomLevels];
      newZoomLevels[currentSlide] = Math.min(
        newZoomLevels[currentSlide] + 0.2,
        3,
      );
      return newZoomLevels;
    });
  };
  const handleZoomOut = () => {
    setZoomLevels((prevZoomLevels) => {
      const newZoomLevels = [...prevZoomLevels];
      newZoomLevels[currentSlide] = Math.max(
        newZoomLevels[currentSlide] - 0.2,
        1,
      );
      return newZoomLevels;
    });
  };

  const PrevArrow = (props) => {
    const { className, style, onClick } = props;
    return (
      <CustomToolTip
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography
              variant="caption"
              color="white"
              fontWeight={600}
              lineHeight="16.39px"
            >
              Previous
            </Typography>
            <ArrowsContainer>
              <ArrowBackIcon fontSize="inherit" />
            </ArrowsContainer>
          </Box>
        }
        arrow={false}
        placement="top"
        PopperProps={{
          sx: {
            '.MuiTooltip-tooltip': {
              marginBottom: '8px !important',
              background: '#212121 !important',
            },
          },
        }}
      >
        <div
          className={className}
          style={{
            ...style,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={onClick}
        />
      </CustomToolTip>
    );
  };

  const NextArrow = (props) => {
    const { className, style, onClick } = props;
    return (
      <CustomToolTip
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography
              variant="caption"
              color="white"
              fontWeight={600}
              lineHeight="16.39px"
            >
              Next
            </Typography>
            <ArrowsContainer>
              <ArrowForwardIcon fontSize="inherit" />
            </ArrowsContainer>
          </Box>
        }
        arrow={false}
        placement="top"
        PopperProps={{
          sx: {
            '.MuiTooltip-tooltip': {
              marginBottom: '8px !important',
              background: '#212121 !important',
            },
          },
        }}
      >
        <div
          className={className}
          style={{
            ...style,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={onClick}
        />
      </CustomToolTip>
    );
  };

  const settings = {
    initialSlide: currentSlide,
    afterChange: (current: number) => {
      setCurrentSlide(current);
      setZoomLevels(dialogData?.allAttachments.map(() => 1));
    },
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
  };

  const currentAttachment = dialogData?.allAttachments[currentSlide];

  const MuiIcon = ({ name, ...rest }) => {
    const IconComponent = iconToComponentMapping[name];
    return IconComponent ? <IconComponent {...rest} /> : null;
  };

  return (
    <CustomModal
      maxWidth={false}
      open={open}
      handleClose={handleClose}
      sx={{
        '.MuiPaper-root': {
          flexDirection: 'row',
          width: { xs: '100%', md: '1400px' },
          height: { xs: '100%', md: '860px' },
        },
      }}
    >
      <Grid container spacing={0}>
        <ImageContainer item xs={12} md={8}>
          <HeaderContent>
            <Typography
              variant="h5"
              fontWeight={700}
              lineHeight="32.78px"
              color="black"
              overflow="hidden"
              noWrap
              textOverflow="ellipsis"
            >
              {currentAttachment?.name}
            </Typography>
            <ButtonsContainer>
              <CustomToolTip
                title="Zoom Out"
                arrow={false}
                placement="top"
                PopperProps={{
                  sx: {
                    '.MuiTooltip-tooltip': {
                      marginBottom: '6px !important',
                      background: '#212121 !important',
                    },
                  },
                }}
              >
                <IconButton onClick={handleZoomOut}>
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
              </CustomToolTip>
              <CustomToolTip
                title="Zoom In"
                arrow={false}
                placement="top"
                PopperProps={{
                  sx: {
                    '.MuiTooltip-tooltip': {
                      marginBottom: '6px !important',
                      background: '#212121 !important',
                    },
                  },
                }}
              >
                <IconButton onClick={handleZoomIn}>
                  <ZoomInIcon fontSize="small" />
                </IconButton>
              </CustomToolTip>
              <CustomToolTip
                title="Download"
                arrow={false}
                placement="top"
                PopperProps={{
                  sx: {
                    '.MuiTooltip-tooltip': {
                      marginBottom: '6px !important',
                      background: '#212121 !important',
                    },
                  },
                }}
              >
                <IconButton
                  onClick={(event) => {
                    event.stopPropagation();
                    downloadFile(
                      currentAttachment?.image,
                      `${getFileName(
                        currentAttachment?.image,
                      )}.${getUrlExtension(currentAttachment?.image)}`,
                    );
                  }}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </CustomToolTip>
              <Divider
                orientation="vertical"
                sx={{ border: '1px solid #E0E0E0', height: '36px' }}
              ></Divider>
              <IconButton onClick={handleClose}>
                <CloseRounded fontSize="small" />
              </IconButton>
            </ButtonsContainer>
          </HeaderContent>
          <Slider {...settings}>
            {dialogData.allAttachments.map((item, index) => (
              <ImageBox key={index}>
                <img
                  src={item?.image}
                  alt={`Slide ${index + 1}`}
                  style={{ transform: `scale(${zoomLevels[index]})` }}
                />
              </ImageBox>
            ))}
          </Slider>
        </ImageContainer>
        <Grid item xs={12} md={4}>
          <Typography
            variant="h6"
            fontWeight={700}
            lineHeight="27.32px"
            color="black"
            p={3}
            pt="34px"
          >
            Details
          </Typography>
          <Divider sx={{ border: '1px solid #E0E0E0' }}></Divider>
          <Stack spacing="20px" p={3}>
            {currentAttachment?.updater && (
              <Stack spacing={0.75}>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  lineHeight="18px"
                  color="#616161"
                >
                  Uploaded By
                </Typography>
                <Box gap="10px" display="flex" alignItems="center">
                  <UserAvatar
                    width={28}
                    height={28}
                    firstName={currentAttachment?.updater?.name}
                    profilePic={currentAttachment?.updater?.photo}
                  />
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    lineHeight="19.12px"
                    color="black"
                  >
                    {currentAttachment?.updater?.name}
                  </Typography>
                </Box>
              </Stack>
            )}
            <Stack spacing="2px">
              <Typography
                variant="body2"
                fontWeight={700}
                lineHeight="18px"
                color="#616161"
              >
                Date/Time
              </Typography>
              <Typography
                variant="body2"
                fontWeight={600}
                lineHeight="24px"
                color="black"
              >
                {moment
                  .tz(currentAttachment?.date, tz)
                  .format('MMM DD, YYYY [at] hh:mm A')}
              </Typography>
            </Stack>
            {currentAttachment?.icon && (
              <Stack spacing={0.75}>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  lineHeight="18px"
                  color="#616161"
                >
                  Template
                </Typography>
                <Box gap="10px" display="flex" alignItems="center">
                  <IconContainer bgcolor={currentAttachment?.icon?.color}>
                    <MuiIcon name={currentAttachment?.icon?.icon} />
                  </IconContainer>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    lineHeight="24px"
                    color="black"
                  >
                    {currentAttachment?.templateName}
                  </Typography>
                </Box>
              </Stack>
            )}
            {currentAttachment?.locations.length > 0 && (
              <Stack spacing={0.25}>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  lineHeight="18px"
                  color="#616161"
                >
                  Location(s)
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  lineHeight="24px"
                  color="black"
                >
                  {currentAttachment?.locations
                    .map((item) => item.name)
                    .join(', ')}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Grid>
      </Grid>
    </CustomModal>
  );
};

export default GalleryDetailsModal;
